import {
	createContext,
	PropsWithChildren,
	useContext,
	useEffect,
	useMemo,
	useState
} from 'react';
import { StateViewResponse, DataState } from '../types/api';
import { PageTypes, useJsonApi } from '../hooks/useJsonApi';
import { useRoute } from './RouteProvider';

/**
 * Finds the data item matching the selected state abbreviation.
 * @param data - Array of DataState items from API
 * @param selectedState - Current state abbreviation (e.g., 'az')
 * @returns Matching DataState or null if not found
 */
function findStateData(
	data: DataState[] | undefined,
	selectedState: string | null
): DataState | null {
	if (!data || !selectedState) return null;

	return (
		data.find((item) => {
			const stateAbbr = item.field_state_reference?.field_abbreviation?.toLowerCase();
			return stateAbbr === selectedState.toLowerCase();
		}) || null
	);
}

export interface DataFormPageContextInterface {
	apiData: StateViewResponse | undefined;
	isFetching: boolean;
	state: string | null;
	setPage: (page: PageTypes) => void;
	page: PageTypes | undefined;
	availableStates: string[] | null;
}

// eslint-disable-next-line react-refresh/only-export-components
export const CONFIG_PAGE_CONTEXT_DEFAULT: DataFormPageContextInterface = {
	apiData: undefined,
	isFetching: false,
	state: null,
	setPage: () => {},
	page: undefined,
	availableStates: null
};

const DataFormPageContext = createContext(CONFIG_PAGE_CONTEXT_DEFAULT);

// eslint-disable-next-line react-refresh/only-export-components
export function useDataFormPage() {
	return useContext<DataFormPageContextInterface>(DataFormPageContext);
}

export interface DataFormPageProviderInterface extends PropsWithChildren {
	customFilterUrl?: string;
}

export function DataFormPageProvider({
	children,
	customFilterUrl
}: DataFormPageProviderInterface) {
	const [page, setPage] = useState<PageTypes | undefined>(undefined);
	const { selectedState, setSelectedState } = useRoute();
	const { isFetching, data, state } = useJsonApi<StateViewResponse>(page, customFilterUrl);

	const apiData = useMemo(() => {
		if (data !== null && !isFetching) {
			// Handle multi-state response: filter by selectedState
			if (Array.isArray(data.data) && data.data.length > 1) {
				const filteredState = findStateData(data.data, selectedState);

				if (filteredState) {
					return { ...data, data: [filteredState] } as StateViewResponse;
				} else {
					// No match found - log warning and fallback to first item
					console.warn(
						`[DataFormPageProvider] State "${selectedState}" not found in API response.`,
						`Available states:`,
						data.data
							.map((d) => d.field_state_reference?.field_abbreviation)
							.filter(Boolean)
					);
					return { ...data, data: [data.data[0]] } as StateViewResponse;
				}
			}

			// Single item or empty array - pass through unchanged
			return data;
		}
		return undefined;
	}, [data, isFetching, selectedState]);

	const availableStates = useMemo(() => {
		if (data?.data && Array.isArray(data.data) && data.data.length > 1) {
			// Multi-state response - extract state abbreviations
			return data.data
				.map((item) => item.field_state_reference?.field_abbreviation)
				.filter(Boolean) // Remove null/undefined
				.map((abbr) => abbr!.toUpperCase());
		}
		return null; // null = all states available (single-state response)
	}, [data]);

	// Automatically navigate to first available state if current selection is invalid
	useEffect(() => {
		if (
			availableStates && // We have a filtered list of states
			availableStates.length > 0 && // List is not empty
			selectedState && // User has a state selected
			!availableStates.includes(selectedState.toUpperCase()) // Selected state is not in the available list
		) {
			// Navigate to the first available state
			const firstAvailableState = availableStates[0].toLowerCase();
			console.log(
				`[DataFormPageProvider] Selected state "${selectedState}" not available. Navigating to "${firstAvailableState}"`
			);
			setSelectedState(firstAvailableState);
		}
	}, [availableStates, selectedState, setSelectedState]);

	return (
		<DataFormPageContext.Provider
			value={{
				apiData,
				isFetching,
				state,
				setPage,
				page,
				availableStates
			}}
		>
			{children}
		</DataFormPageContext.Provider>
	);
}
